import { Autocomplete, ChipProps } from '@mui/material';

import { TextFieldProps } from '../TextField';
import { DEFAULT_SEPARATOR } from './const';
import { useAutocomplete } from './hooks/useAutocomplete';
import { useInput } from './hooks/useInput';
import {
  AutocompleteProps,
  DisableClearable,
  FreeSolo,
  GetChipProps,
  Multiple,
} from './types';

type PropsToExtend<Value> = Omit<
  AutocompleteProps<Value>,
  'onChange' | 'options' | 'renderInput'
>;

export interface ChipsTextFieldProps<Value = string[]>
  extends PropsToExtend<Value> {
  allowIdenticalChips?: boolean;
  chipProps: Partial<ChipProps>;
  forbiddenSymbols?: string[];
  getChipProps?: GetChipProps<Value>;
  hasCounter?: boolean;
  maxLength?: number;
  onChange?: (value: Value[]) => void;
  separators?: string[];
  textFieldProps?: Partial<TextFieldProps>;
}

const isOptionEqualToValue = (): boolean => false;

export function ChipsTextField<Value>({
  allowIdenticalChips = false,
  chipProps,
  forbiddenSymbols,
  getChipProps,
  hasCounter,
  maxLength,
  onChange: handleChange,
  separators = [DEFAULT_SEPARATOR],
  textFieldProps,
  value: initialValue,
  ...autoCompleteProps
}: ChipsTextFieldProps<Value>) {
  const { defaultValue } = autoCompleteProps;

  const { handleAddValue, value, onChange, renderInput, renderTags } =
    useAutocomplete({
      chipProps,
      defaultValue: defaultValue as Value[],
      getChipProps,
      handleChange,
      hasCounter,
      initialValue: initialValue as Value[],
      maxLength,
      textFieldProps,
    });

  const { inputValue, onInputBlur, onInputChange } = useInput({
    addAutocompleteValue: handleAddValue,
    separators,
    forbiddenSymbols,
  });

  return (
    <Autocomplete<Value, Multiple, DisableClearable, FreeSolo>
      {...autoCompleteProps}
      disableClearable
      freeSolo
      // This option produces a warning in dev mode that a textarea element is
      // not appropriate for using in the Autocomplete component.
      // It actually works fine and there is no way to get rid of the warning.
      // Reference: https://github.com/mui/material-ui/issues/29961#issuecomment-1234191616
      multiple
      onChange={onChange}
      options={[]}
      onBlur={onInputBlur}
      renderInput={renderInput}
      renderTags={renderTags}
      onInputChange={onInputChange}
      inputValue={inputValue}
      isOptionEqualToValue={
        allowIdenticalChips ? isOptionEqualToValue : undefined
      }
      value={value}
    />
  );
}
